import React from "react"
import useLang from "../../hooks/useLang"
import FadeInUp from "../animate/FadeInUp"
import "./vmv.css"
import { Box } from "@chakra-ui/react"

const defaultData = {
  vision: {
    titleAr: "الرؤية",
    titleEn: "Vision",
    textAr: "نسعى لأن نكون من روّاد الحلول التقنية على مستوى العالم، من خلال الابتكار والجودة وبناء شراكات مستدامة.",
    textEn: "We seek To be one of the best IT solution providers in the Middle East region",
  },
  mission: {
    titleAr: "المهمة",
    titleEn: "Mission",
    textAr:
      "نلتزم بتقديم حلول تقنية مبتكرة ومتكاملة تمكّن عملاءنا من تحقيق أهدافهم، وتضمن رضاهم، وتفوق توقعاتهم بشكل مستمر.",
    textEn:
      "We are committed in developing innovative ideas and complete solutions to achieve customer satisfaction and exceed their expectations",
  },
  values: {
    titleAr: "القيم الأساسية",
    titleEn: "Values",
    itemsAr: [
      {title: "الجودة", description: "نلتزم بتقديم أعلى معايير الجودة في جميع خدماتنا وحلولنا."}, 
      {title: "الالتزام", description: "نحرص على الوفاء بوعودنا وتحقيق نتائج موثوقة ومستدامة."}, 
      {title: "الابتكار", description: "نسعى باستمرار لتطوير أفكار وحلول مبتكرة تواكب تطور السوق."}, 
      {title: "رضا العملاء", description: "رضا نضع رضا عملائنا في مقدمة أولوياتنا، ونسعى دائمًا لتجاوز توقعاتهم."}, 
      {title: "النجاح المستمر", description: "نؤمن بأهمية التطوير المستمر لتحقيق النجاح والنمو على المدى الطويل."}
    ],
    itemsEn: [
      {title: "Quality", description: "We are committed to providing the highest quality standards in all our services and solutions."},
      {title: "Commitment", description: "We strive to fulfill our commitments and achieve reliable and sustainable results."},
      {title: "Innovation", description: "We continuously seek to develop innovative ideas and solutions that keep pace with market development."},
      {title: "Customer Satisfaction", description: "We place customer satisfaction at the top of our priorities, constantly striving to exceed their expectations."},
      {title: "Continued Success", description: "We believe in the importance of continuous development to achieve success and growth in the long term."},
    ]
  },
}

const VisionIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
    <circle cx="12" cy="12" r="3.2" />
  </svg>
)

const MissionIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" />
    <circle cx="12" cy="12" r="4.5" />
    <path d="M12 3.5V1.75M20.5 12H22.25M12 20.5v1.75M1.75 12H3.5" />
  </svg>
)

const ValuesIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3 20 8 12 13 4 8 12 3Z" />
    <path d="M4 12l8 5 8-5" />
    <path d="M4 16l8 5 8-5" />
  </svg>
)

const VisionMissionValues = ({ vision, mission, values }) => {
  const isAr = useLang() === "ar"

  const data = {
    vision: vision || defaultData.vision,
    mission: mission || defaultData.mission,
    values: values || defaultData.values,
  }

const valuesList = (
  isAr ? data.values.itemsAr : data.values.itemsEn
)?.filter(Boolean) || []

  const copy = isAr
    ? {
        eyebrow: "قيمنا الأساسية",
        title: "الرؤية والرسالة والقيم الأساسية",
        lead:
          "رؤيتنا تحدد الاتجاه، ورسالتنا توضّح ما نقدمه، وقيمنا تشكل طريقة تعاملنا مع كل مشروع وكل عميل.",
        // valuesLabel: "الأساس",
        valuesText:
          "هذه المبادئ هي المرجع الذي يوجّه طريقة التفكير والتنفيذ والتواصل في جميع أعمالنا.",
      }
    : {
        eyebrow: "OUR FOUNDATION",
        title: "The Vision, Mission, and Values",
        lead:
          "Our vision sets the direction, our mission defines what we deliver, and our values shape every client interaction.",
        // valuesLabel: "Foundation",
        valuesText:
          "These principles serve as the reference that guides our mindset, execution, and communication across all our endeavors",
      }

  const cards = [
    {
      key: "vision",
      icon: <VisionIcon />,
      // label: copy.visionLabel,
      title: isAr ? data.vision.titleAr : data.vision.titleEn,
      text: isAr ? data.vision.textAr : data.vision.textEn,
    },
    {
      key: "mission",
      icon: <MissionIcon />,
      // label: copy.missionLabel,
      title: isAr ? data.mission.titleAr : data.mission.titleEn,
      text: isAr ? data.mission.textAr : data.mission.textEn,
    },
  ]

  return (
    <section className="bm-vmv" dir={isAr ? "rtl" : "ltr"}>
      <div className="container">
        <div className="bm-vmv__shell">
          <FadeInUp>
            <div className="bm-vmv__intro">
              <Box className="about-badge-wrapper">
                <span className="about-badge">
                  <span className="about-badge-dot" />
                  {copy.eyebrow}
                </span>
              </Box>

              <h2 className="bm-vmv__headline">{copy.title}</h2>
              <p className="bm-vmv__lead">{copy.lead}</p>
            </div>
          </FadeInUp>

          <div className="bm-vmv__cards">
            {cards.map(card => (
              <FadeInUp key={card.key}>
                <div className="bm-vmv-card-wrap">
                <article className="bm-vmv-card">
                  <div className="bm-vmv-card__head">
                    <div className="bm-vmv-card__icon">{card.icon}</div>

                    <div className="bm-vmv-card__meta">
                      {/* <span className="bm-vmv-card__label">{card.label}</span> */}
                      <h3 className="bm-vmv-card__title">{card.title}</h3>
                    </div>
                  </div>

                  <p className="bm-vmv-card__text">{card.text}</p>
                </article>
                </div>
              </FadeInUp>
            ))}
          </div>

          <FadeInUp>
            <div className="bm-vmv-values">
              <div className="bm-vmv-values__count">
                {String(valuesList.length).padStart(2, "0")}
              </div>

              <div className="bm-vmv-values__layout">
                <div className="bm-vmv-values__summary">
                  <div className="bm-vmv-values__icon">
                    <ValuesIcon />
                  </div>

                  {/* <span className="bm-vmv-values__label">{copy.valuesLabel}</span> */}
                  <h3 className="bm-vmv-values__title">
                    {isAr ? data.values.titleAr : data.values.titleEn}
                  </h3>
                  <p className="bm-vmv-values__text">{copy.valuesText}</p>
                </div>

                <div className="bm-vmv-values__grid">
            {valuesList.map((item, index) => (
              <article className="bm-vmv-value" key={`${typeof item === 'object' ? item.title : item}-${index}`}>
                <div className="bm-vmv-values-wrapper">
                  <span className="bm-vmv-value__index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h4 className="bm-vmv-value__title">
                    {typeof item === 'object' ? item.title : item}
                  </h4>
                </div>

                <div className="description">
                  <p>{typeof item === 'object' ? item.description : ''}</p>
                </div>
              </article>
            ))}
                </div>
              </div>
            </div>
          </FadeInUp>
        </div>
      </div>
    </section>
  )
}

export default VisionMissionValues
